import { joiResolver } from "@hookform/resolvers/joi";
import {
  Alert,
  Box,
  Button,
  Col,
  Divider,
  Flex,
  Grid,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput, DatePicker, DatePickerInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCheck,
  IconCircleCheck,
  IconCoinRupee,
  IconCurrencyRupee,
  IconEdit,
  IconPlus,
} from "@tabler/icons";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import Joi from "joi";
import moment from "moment";
import { getCounterId } from "pages/Auth/permissionGuard";
import { invoice_api } from "pages/transactions/invoice/invoice.service";
import {
  api_addPaymentReceipt,
  api_editPaymentReceipt,
  api_getPaymentReceipt,
} from "pages/transactions/payment-receipt/paymentReceipt.service";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import CustomerSelection from "./CustomerSelection";

const SelectItem = forwardRef(({ label, balance_amount, ...others }, ref) => (
  <div ref={ref} {...others}>
    <Flex justify="space-between" align="center">
      <Text size="sm">{label}</Text>
      {balance_amount !== undefined && (
        <Text size="xs" color="dimmed">
          (Bal: ₹{balance_amount})
        </Text>
      )}
    </Flex>
  </div>
));


function AddPaymentReceipt({ opened, onClose, invoiceId, defaultData, edit, fromInvoice, isPaymentReceiptPage }) {
  const amountRef = useRef(null);
  const paymentTypeRef = useRef(null);
  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  const [counter, setCounter] = useState(getCounterId());
  const [balanceAmount, setBalanceAmount] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invoiceData, setInvoiceData] = useState({});
  const [rawInvoices, setRawInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remianingAmount, setRemianingAmount] = useState(null);

  const [customerId, setCustomerId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedInvoicesData, setSelectedInvoicesData] = useState([]);

  const emptyMessage = label => {
    return {
      "string.empty": `${label} is required.`,
      "number.base": `${label} is required.`,
    };
  };

  const validationSchema = Joi.object({
    invoice: Joi.alternatives().try(Joi.number(), Joi.array().items(Joi.number())).required().messages(emptyMessage("Invoice")),
    date: Joi.date().required().messages(emptyMessage("Date")),
    payment_type: Joi.string().required().messages(emptyMessage("Payment Type")),
    amount: Joi.number().required().messages(emptyMessage("Amount")),
  });

  const {
    control,
    register: addPaymentReceiptForm,
    handleSubmit,
    reset,
    setValue,
    setValues,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      invoice: isPaymentReceiptPage && !edit ? [] : null,
      date: new Date(),
      payment_type: "cash",
      amount: "",
    },
    resolver: joiResolver(validationSchema),
  });

  // Getting the invoices
  const invoices_data = useQuery({
    queryKey: ["invoices_list", counter, isPaymentReceiptPage && !edit ? customerId : null],
    queryFn: () => invoice_api.get_invoices(counter, "unlimited", 1, 100, isPaymentReceiptPage && !edit ? customerId : null),
    onSuccess: data => {
      setRawInvoices(data.data);
      const invoice = data.data.map(e => {
        return {
          id: e.id,
          label: e.invoice_id ? e.invoice_id : "##" + e.id,
          value: e.id,
          balance_amount: e.balance_amount !== undefined ? e.balance_amount : 0,
        };
      });
      console.log(invoice)
      setInvoices(invoice);
    },
  });

  // Setting the data from selected invoices
  const handleInvoiceSelect = async ids => {
    const isArray = Array.isArray(ids);
    const idList = isArray ? ids : [ids];
    setValue("invoice", ids);

    let selectedData = [];
    for (const id of idList) {
      let invoice = rawInvoices.find(e => e.id === +id);
      if (!invoice) {
        try {
          const invoice_data = await invoice_api.get_invoice_by_id(id);
          invoice = invoice_data.data.data;
        } catch (e) {
          console.error("error fetching invoice", id, e);
        }
      }
      if (invoice) selectedData.push(invoice);
    }

    setSelectedInvoicesData(selectedData);
    
    // For single select compatibility (if needed by other parts of the code)
    if (!isArray && selectedData.length > 0) {
      setInvoiceData(selectedData[0]);
    } else if (selectedData.length === 0) {
      setInvoiceData({});
    }

    if (selectedData.length > 0) {
      // Set customer from the first selected invoice if not already set
      if (!customerId) setCustomerId(selectedData[0].user_id);
    }

    await calculateBalanceAmount(selectedData);
  };

  // Calculate Balance amount
  const calculateBalanceAmount = async selectedData => {
    setLoading(true);
    if (!selectedData || selectedData.length === 0) {
      setBalanceAmount(0);
      setLoading(false);
      return;
    }

    let totalBalance = 0;
    try {
      const updatedDataWithBalances = await Promise.all(
        selectedData.map(async invoice => {
          const resolvedId = invoice.id || invoice.invoice;
          const res = await api_getPaymentReceipt(counter, resolvedId);
          const total_receipt_amount = res.data.reduce((sum, { amount }) => sum + amount, 0);
          const balance = +(invoice.total || 0).toFixed(0) - +total_receipt_amount.toFixed(0);
          totalBalance += balance;
          return { ...invoice, balance };
        })
      );
      setSelectedInvoicesData(updatedDataWithBalances);
      setBalanceAmount(totalBalance);
    } catch (e) {
      console.error("Error calculating balances", e);
    } finally {
      setLoading(false);
    }
  };

  // Calculate Remaining amount
  const calculateRemianingAmount = amount => {
    setValue("amount", +amount);
    setRemianingAmount(amount - balanceAmount);
  };

  // Adding the payment receipt
  const addPaymentReceipt = async values => {
    setLoading(true);
    const invoiceIds = Array.isArray(values.invoice) ? values.invoice : [values.invoice];
    let remainingAmount = +values.amount;

    try {
      for (const invId of invoiceIds) {
        if (remainingAmount <= 0) break;

        const invData = selectedInvoicesData.find(i => {
          const id = i.id || i.invoice;
          return id === +invId;
        });

        if (!invData) continue;

        const balance = invData.balance || 0;
        if (balance <= 0) continue;

        const payAmount = Math.min(remainingAmount, balance);
        
        let payload = {
          amount: payAmount,
          date: moment(values.date).format("YYYY-MM-DD"),
          invoice_id: invId,
          mode_of_payment: values.payment_type,
        };

        const res = await api_addPaymentReceipt(payload);
        if (res.success) {
          await deliverInvoice(invId);
          remainingAmount -= payAmount;
        } else {
          showErrorToast({
            title: "Error adding payment for Invoice " + (invData.invoice_id || invId),
            message: res.message,
          });
          // Depending on requirements, we could break or continue. Let's break to be safe.
          setLoading(false);
          return;
        }
      }

      showSuccessToast({
        title: "Success",
        message: "Payment Receipt(s) successfully added.",
      });
      handleExit();
    } catch (error) {
      console.error("Error in addPaymentReceipt:", error);
      showErrorToast({ title: "Error", message: "An unexpected error occurred." });
    } finally {
      setLoading(false);
    }
  };

  // Editing the payment receipt
  const editPaymentReceipt = async values => {
    setLoading(true);

    let payload = {
      payment_receipt_id: defaultData.id,
      amount: values.amount,
      date: moment(values.date).format("YYYY-MM-DD"),
      mode_of_payment: values.payment_type,
    };

    console.log(payload);

    await api_editPaymentReceipt(payload).then(res => {
      setLoading(false);

      if (res.success) {
        showSuccessToast({
          title: "Success",
          message: "Payment receipt succesfully edited.",
        });
        handleExit();
      } else {
        showErrorToast({
          title: "Error",
          message: res.message,
        });
      }
    });
  };

  const deliverInvoice = async id => {
    await invoice_api.deliver_invoice(id).then(res => {
      console.log(res.data);
      if (res.data.success) {
        showSuccessToast({
          title: "Success",
          message: res.data.message,
        });
        invoices.refetch();
      } else {
        showErrorToast({
          title: "Error",
          message: res.data.message,
        });
      }
    });
  };

  //Invoice Flow starts from this useEffect
  useEffect(() => {
    if (invoiceId) {
      console.log({ defaultData });
      const setData = async () => {
        setInvoiceData(defaultData);
        setSelectedInvoicesData([defaultData]);
        setCustomerId(defaultData.user_id);
        await calculateBalanceAmount([defaultData]);
      };
      setData();
      setValue("invoice", invoiceId);
      setTimeout(() => {
        console.log("amount ref",amountRef);
        amountRef.current?.focus();
      }, 100);
      console.log(defaultData);

      // if (fromInvoice) {
      // setValue("amount", defaultData.total);
      // }
    }
  }, [invoiceId, defaultData]);

  //Default Data from payment receipt only and payment receipt flow starts from this
  useEffect(() => {
    if (edit) {
      console.log({ defaultData });
      setValue("invoice", defaultData.invoice);
      setValue("amount", defaultData.amount);
      setValue("date", new Date(defaultData.date));
      setValue("payment_type", defaultData.payment_type);

      const setData = async () => {
        // await calculateBalanceAmount(defaultData.invoice_id?defaultData.total.toFixed(0):defaultData.amount, invoiceId);
        setCustomerId(defaultData.user_id);
        await handleInvoiceSelect(defaultData.invoice);
      };

      setData();
    }
  }, [edit]);

  // const handleKeyDown = (e) => {
  //   if (e.key === "Enter") {
  //     e.preventDefault(); // Prevent the default form submission (if any)
  //     handleSubmit((values) => {
  //       console.log(values);
  //       edit ? editPaymentReceipt(values) : addPaymentReceipt(values);
  //     })();
  //   }
  // };

  const handleExit = () => {
    reset({
      invoice: isPaymentReceiptPage && !edit ? [] : null,
      date: new Date(),
      payment_type: "cash",
      amount: "",
    });
    setInvoiceData({});
    setSelectedInvoicesData([]);
    setBalanceAmount(null);
    setRemianingAmount(null);
    setCustomerId(null);
    onClose();
  };

/*  // useEffect(() => {
  //   const handleExitShortcut = (e) => {
  //     if (e.key === "Escape" || e.key === "F8") {
  //       e.preventDefault();
  //       handleExit(); // ✅ existing flow only
  //     }
  //   };

  //   window.addEventListener("keydown", handleExitShortcut);

  //   return () => {
  //     window.removeEventListener("keydown", handleExitShortcut);
  //   };
  // }, []);


  // useEffect(() => {
  //   const handleF7 = (e) => {
  //     if (e.key === "F7") {
  //       e.preventDefault();

  //       handleSubmit((values) => {
  //         edit
  //           ? editPaymentReceipt(values)
  //           : addPaymentReceipt(values, true); // pass print flag
  //       })();
  //     }
  //   };

  //   window.addEventListener("keydown", handleF7);
  //   return () => window.removeEventListener("keydown", handleF7);
  // }, [balanceAmount, edit]);
*/

  //keyboard shortcuts useEffect
  useEffect(() => {
    if (!opened) return; // 🔴 important: only active when modal is open

    const handleKeyDown = (e) => {
      // EXIT (ESC or F8)
      if (e.key === "F8") {
        e.preventDefault();
        e.stopPropagation(); // 🔴 prevents parent page from reacting
        handleExit();
      }

      // SAVE / PRINT (F7)
      if (e.key === "F7") {
        e.preventDefault();
        e.stopPropagation();

        handleSubmit((values) => {
          edit
            ? editPaymentReceipt(values)
            : addPaymentReceipt(values);
        })();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [opened, edit, balanceAmount]);


  return (
    <div>
      <Modal
        opened={opened}
        withCloseButton={false}
        onClose={handleExit}
        closeOnEscape={false}
        closeOnClickOutside={false}
        styles={{ header: { borderBottom: "1px solid #eee" }, body: { padding: 0 } }}
        title={
          <Flex justify="space-between" align="center" w="100%" px={10}>
            <Text fw={600}>Add New Payment Receipt</Text>

            <Button
              size="xs"
              variant="filled"
              color="red"
              onClick={handleExit}
              style={{ position: "absolute", right: 16, top: 12 }}
            >
              EXIT (F8)
            </Button>
          </Flex>
        }
        size="800px"
        centered
      >
        <LoadingOverlay visible={loading} />
        <Box>
          <Box my={20} px={30}>
            <Grid>
              <Col span={isMobileScreen ? 6 : 2}>
                <Text c="dimmed" fw={500} size={"xs"}>
                  Invoice No.
                </Text>
                <Text>
                  {selectedInvoicesData.length > 1
                    ? `${selectedInvoicesData.length} Selected`
                    : selectedInvoicesData[0]?.invoice_id || "None"}
                </Text>
              </Col>
              <Col span={isMobileScreen ? 6 : 2}>
                <Text c="dimmed" fw={500} size={"xs"}>
                  Round Off
                </Text>
                <Text>
                  {(() => {
                    let totalRoundOff = 0;
                    selectedInvoicesData.forEach(inv => {
                      let total = inv.total ?? 0;
                      let roundedTotal = total % 1 >= 0.5 ? Math.ceil(total) : Math.floor(total);
                      totalRoundOff += roundedTotal - total;
                    });
                    return (totalRoundOff > 0 ? "+" : "") + totalRoundOff.toFixed(2);
                  })()}
                </Text>
              </Col>

              <Col span={isMobileScreen ? 6 : 2}>
                <Text c="dimmed" fw={500} size={"xs"}>
                  Net Amount
                </Text>
                <Text>
                  ₹{" "}
                  {(() => {
                    let totalNetAmount = 0;
                    selectedInvoicesData.forEach(inv => {
                      let total = inv.total ?? 0;
                      let roundedTotal = total % 1 >= 0.5 ? Math.ceil(total) : Math.floor(total);
                      totalNetAmount += roundedTotal;
                    });
                    return totalNetAmount.toFixed(2);
                  })()}
                </Text>
              </Col>
              <Col span={isMobileScreen ? 6 : 2}>
                <Text c="dimmed" fw={500} size={"xs"}>
                  Balance Amount
                </Text>
                <Text>₹ {balanceAmount || 0}</Text>
              </Col>

              {/* <Col span={isMobileScreen ? 6 : 4}>
                <Flex justify={"end"}>
                  <Box>
                    <Text c="primary" fw={500} size={"xs"}>
                      Handover Amount
                    </Text>
                    <Text fw={500} size={"lg"}>
                      ₹&nbsp;
                      {+getValues("amount") - +balanceAmount > 0
                        ? (+getValues("amount") - +balanceAmount).toFixed(2)
                        : 0}
                    </Text>
                  </Box>
                </Flex>
              </Col> */}

              <Col span={isMobileScreen ? 6 : 4}>
                <Flex justify={"end"}>
                  <Box>
                    <Text c="primary" fw={500} size={"xs"}>
                      Handover Amount
                    </Text>
                    <Text fw={500} size={"lg"}>
                      ₹&nbsp;
                      {(() => {
                        let total = invoiceData?.total ?? 0;
                        let roundedTotal =
                          total % 1 >= 0.5 ? Math.ceil(total) : Math.floor(total);
                        let paid = roundedTotal - balanceAmount;
                        let roundedBalance = roundedTotal - paid;

                        let handover = fromInvoice && roundedBalance !== 0 ? +getValues("amount") - roundedBalance : 0;
                        //create console for all the values here
                        // console.log(
                        //   "total",
                        //   total,
                        //   "roundedTotal",
                        //   roundedTotal,
                        //   "paid",
                        //   paid,
                        //   "roundedBalance",
                        //   roundedBalance,
                        //   "handover",
                        //   handover,
                        //   "balanceAmount",
                        //   balanceAmount,
                        //   "getValues",
                        //   getValues("amount"),
                        //   "invoiceData",
                        //   invoiceData,
                        //   defaultData.id
                        // )
                        return handover > 0 ? handover.toFixed(2) : 0;
                      })()}
                    </Text>
                  </Box>
                </Flex>
              </Col>


              {/* <Col span={2}>
                <Flex justify={"end"}>
                  <Box>
                    <Text c="dimmed" fw={500} size={"xs"}>
                      Remaining Amount
                    </Text>
                    <Text>₹ {+remianingAmount?.toFixed(2) || 0}</Text>
                  </Box>
                </Flex>
              </Col> */}
            </Grid>
          </Box>

          <Divider opacity={0.5} />

          { (
            <Box mt={20} mb={40} px={30}>
              <Text c="dimmed" fw={500} size={"sm"} mb={10}>
                {edit ? "Edit Payment Receipt" : "Add Payment Receipt"}
              </Text>

              <form
                onError={() => {
                  console.log(errors);
                }}
                onSubmit={handleSubmit(values => {
                  console.log(values);
                  edit ? editPaymentReceipt(values) : addPaymentReceipt(values);
                })}

                // onKeyDown={handleKeyDown} // Listen for the Enter key
              >
                <Grid>
                  {isPaymentReceiptPage && !edit && (
                    <Col sm={6}>
                      <CustomerSelection
                        disableAutoSelectCash={true}
                        onChange={e => {
                          setCustomerId(e);
                          setValue("invoice", isPaymentReceiptPage && !edit ? [] : null);
                          setInvoiceData({});
                          setSelectedInvoicesData([]);
                          setBalanceAmount(null);
                          setRemianingAmount(null);
                        }}
                        id={customerId}
                        setCustomers={setCustomers}
                      />
                    </Col>
                  )}
                  <Col sm={6}>
                    <Controller
                      name="invoice"
                      control={control}
                      render={({ field }) =>
                        !isPaymentReceiptPage || edit ? (
                          <Select
                            itemComponent={SelectItem}
                            disabled={edit || (!isPaymentReceiptPage && !!invoiceId)}
                            withinPortal
                            data={invoices || []}
                            searchable
                            label="Invoice"
                            placeholder="Select Invoice"
                            error={errors.invoice?.message}
                            {...field}
                            onChange={async e => {
                              await handleInvoiceSelect(e);
                            }}
                          />
                        ) : (
                          <MultiSelect
                            itemComponent={SelectItem}
                            disabled={edit || fromInvoice}
                            withinPortal
                            data={invoices || []}
                            searchable
                            label="Invoice"
                            placeholder="Select Invoice(s)"
                            error={errors.invoice?.message}
                            {...field}
                            onChange={async e => {
                              await handleInvoiceSelect(e);
                            }}
                          />
                        )
                      }
                    />
                  </Col>
                  <Col sm={6}>
                    <Controller
                      name="date"
                      control={control}
                      render={({ field }) => (
                        <DatePickerInput
                          disabled
                          withinPortal
                          popoverProps={{ withinPortal: true }}
                          label="Date"
                          valueFormat="DD-MM-YYYY"
                          placeholder="Select Date"
                          error={errors.date?.message}
                          {...field}
                        />
                      )}
                    />
                  </Col>
                  <Col sm={6}>
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field }) => (
                        <NumberInput
                          disabled={edit}
                          icon={<IconCurrencyRupee size={16} />}
                          type="number"
                          hideControls
                          precision={2}
                          // max={+balanceAmount}
                          // withinPortal
                          label="Amount"
                          placeholder="Enter Amount"
                          error={errors.amount?.message}
                          {...field}
                          ref={amountRef}
                          onChange={e => {
                            calculateRemianingAmount(e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation(); // important
                              if(!e.target.value){
                                return
                              }
                              paymentTypeRef.current?.focus();
                            }
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col sm={6}>
                      <Controller
                        name="payment_type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            data={[
                              { label: "Cash", value: "cash" },
                              { label: "Card", value: "card" },
                              { label: "Upi", value: "upi" },
                              { label: "Gift", value: "gift" },
                            ]}
                            searchable
                            label="Payment Type"
                            placeholder="Select Payment Type"
                            error={errors.payment_type?.message}
                            {...field}
                            ref={paymentTypeRef}
                        
                          />
                        )}
                        // onKeyDown={(e) => {
                        //   if (e.key === "Enter") {
                        //     e.preventDefault();
                        //     e.stopPropagation();

                        //     const dropdownOpen =
                        //       document.querySelector('[data-mantine-select-dropdown]') !== null;

                        //     if (dropdownOpen) {
                        //       // Let Mantine select the highlighted option
                        //       return;
                        //     }

                        //     // Dropdown closed → Submit form
                        //     handleSubmit((values) => {
                        //       edit
                        //         ? editPaymentReceipt(values)
                        //         : addPaymentReceipt(values);
                        //     })();
                        //   }
                        // }}
                        
                      />
                  </Col>

                  <Col sm={12}>
                    <Button
                      w={"100%"}
                      leftIcon={edit ? <IconEdit size={16} /> : <IconPlus size={16} />}
                      mt={10}
                      type="submit"
                    >
                      {edit ? "Edit Payment Receipt(F7)" : "Add Payment Receipt(F7)"}
                    </Button>
                  </Col>
                </Grid>
              </form>
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default AddPaymentReceipt;
